import CustomModal from "components/common/modal";
import TableWrapper from "components/common/table-wrapper";
import {
  alertVariants,
  DEBOUNCE_DELAY,
  initialMetaForTable,
} from "constants/common";
import {
  CaretDown,
  CaretUp,
  Check,
  Key,
  Pencil,
  Plus,
  Trash,
  TrashSimple,
  UploadSimple,
} from "phosphor-react";
import React, { useCallback, useEffect, useState, useMemo } from "react";
import AddUserForm from "./AddUserForm";
import Tooltip from "components/common/tooltip";
import {
  createUser,
  deleteBulkUser,
  updateUser,
  deleteUser,
  getUserByType,
  sendUserConfirmationEmail,
  updateUserPassword,
} from "containers/users/api";
import { getUserInfo, highlightFirstMatch } from "utils/common";
import ChangePasswordForm from "./ChangePasswordForm";
import { toast } from "react-toastify";
import CustomToast from "components/common/custom-toast";

const BASE_USER_COLUMNS = [
  {
    columnKey: "full_name",
    label: "Name",
    isSortable: true,
    defaultSort: "asc",
  },
  { columnKey: "email", label: "Email", isSortable: false },
  { columnKey: "title", label: "Title", isSortable: false },
  {
    columnKey: "status",
    label: "Status",
    isSortable: false,
    isFilterable: true,
  },
  {
    columnKey: "last_login",
    label: "Last Login",
    isSortable: true,
    sortKey: "last_login",
  },
];

const PROGRAM_HOUSING_CS_COLUMNS = [
  BASE_USER_COLUMNS[0],
  BASE_USER_COLUMNS[1],
  BASE_USER_COLUMNS[2],
  { columnKey: "state", label: "States", isSortable: false },
  { columnKey: "counties", label: "Counties", isSortable: false },
  BASE_USER_COLUMNS[3],
  BASE_USER_COLUMNS[4],
];

const EXECUTIVE_ACCOUNTING_COLUMNS = [
  BASE_USER_COLUMNS[0],
  BASE_USER_COLUMNS[1],
  BASE_USER_COLUMNS[2],
  // { columnKey: "state", label: "States", isSortable: false },
  // { columnKey: "counties", label: "Counties", isSortable: false },
  BASE_USER_COLUMNS[3],
  BASE_USER_COLUMNS[4],
];

const USER_TYPE_COLUMNS_MAP = {
  program_coordinator: PROGRAM_HOUSING_CS_COLUMNS,
  housing_navigator: PROGRAM_HOUSING_CS_COLUMNS,
  cs_manager: PROGRAM_HOUSING_CS_COLUMNS,
  executive: EXECUTIVE_ACCOUNTING_COLUMNS,
  accounting: EXECUTIVE_ACCOUNTING_COLUMNS,
  default: PROGRAM_HOUSING_CS_COLUMNS,
};

const SortableTableHeader = ({
  columnKey,
  label,
  isSortable,
  sortConfig,
  onSortChange,
}) => {
  if (!isSortable) {
    return (
      <th>
        <div>{label}</div>
      </th>
    );
  }

  const actualSortKey = sortConfig.sortKey || sortConfig.key;
  const isSorting = actualSortKey === columnKey;
  const isAscending = isSorting && sortConfig.direction === "asc";
  const isDescending = isSorting && sortConfig.direction === "desc";

  const handleSort = () => {
    let direction = "asc";

    if (isSorting) {
      if (isAscending) {
        direction = "desc";
      } else if (isDescending) {
        direction = "asc";
      }
    } else {
      direction = "asc";
    }

    onSortChange(columnKey, direction);
  };

  return (
    <th onClick={handleSort} style={{ cursor: "pointer" }}>
      <div className="d-flex position-relative align-items-center">
        <div>{label}</div>
        <div className="d-flex flex-column ms-2">
          <CaretUp
            size={12}
            color={isAscending ? "#000000" : "#00000050"}
            weight="fill"
          />
          <CaretDown
            color={isDescending ? "#000000" : "#00000050"}
            size={12}
            weight="fill"
          />
        </div>
      </div>
    </th>
  );
};

const renderMultiValues = ({ values }) => {
  if (!values || values.length === 0) {
    return null;
  }

  const MAX_DISPLAY_COUNT = 2;
  const itemsToDisplay = values.slice(0, MAX_DISPLAY_COUNT);
  const remainingCount = values.length - MAX_DISPLAY_COUNT;

  return (
    <>
      {itemsToDisplay.map((item, index) => (
        <span key={index} className="d-inline-block me-1">
          {item.name}
          {index < itemsToDisplay.length - 1 ? ", " : ""}
        </span>
      ))}
      {remainingCount > 0 && (
        <span className="text-secondary ms-1">{`+${remainingCount} more`}</span>
      )}
    </>
  );
};

const getUserKey = (response, userType) => {
  const expectedKeys = {
    program_coordinator: "program_coordinators",
    housing_navigator: "housing_navigators",
    cs_manager: "cs_managers",
    executive: "executives",
    accounting: "accountings",
  };

  const foundKey = Object.keys(response).find((key) =>
    [
      "program_coordinators",
      "housing_navigators",
      "cs_managers",
      "executives",
      "accounting",
      "accountings",
    ].includes(key),
  );

  return foundKey;
};

const sortUsers = (users, key, direction) => {
  if (!key || !direction || !users || users.length === 0) return users;

  const compare = (a, b) => {
    let aValue = a[key] === null || a[key] === undefined ? "" : String(a[key]);
    let bValue = b[key] === null || b[key] === undefined ? "" : String(b[key]);

    if (key === "last_login_at") {
      aValue = new Date(aValue).getTime() || 0;
      bValue = new Date(bValue).getTime() || 0;
    } else {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (aValue < bValue) {
      return direction === "asc" ? -1 : 1;
    }
    if (aValue > bValue) {
      return direction === "asc" ? 1 : -1;
    }
    return 0;
  };

  return [...users].sort(compare);
};

let timeOut;

const Users = ({ userType, meta, setMeta, logoutHandler }) => {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addUserModalVisible, setAddUserModalVisible] = useState(false);
  const [users, setUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentUserEdit, setCurrentUserEdit] = useState(null);
  const [refreshPage, setRefreshPage] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [
    isForgetPasswordModalVisisble,
    setForgetPasswordModalVisible,
  ] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "full_name",
    direction: "asc",
  });
  const handleRefreshPage = () => {
    setRefreshPage((pre) => !pre);
  };
  const toggleForgetPasswordModal = () => {
    setForgetPasswordModalVisible((prev) => !prev);
  };
  const isUserConfirmed = (user) => {
    return Boolean(
      user?.isConfirmed || user?.email_confirmed || user?.confirmed_at,
    );
  };

  const USER_COULUMNS = useMemo(() => {
    return USER_TYPE_COLUMNS_MAP[userType] || USER_TYPE_COLUMNS_MAP.default;
  }, [userType]);

  const getUserValue = (user, columnKey) => {
    switch (columnKey) {
      case "full_name":
        return highlightFirstMatch(user.full_name, meta.search) || "";
      case "email":
        return user.email || "";
      case "title":
        return user.type || "";
      case "state":
        const stateData = Array.isArray(user.states)
          ? user.states
          : user.states && typeof user.states === "object"
          ? [user.states]
          : [];
        return stateData.length > 0
          ? renderMultiValues({ values: stateData })
          : "";
      case "counties":
        const countyData = Array.isArray(user.counties)
          ? user.counties
          : user.counties && typeof user.counties === "object"
          ? [user.counties]
          : [];
        return countyData.length > 0
          ? renderMultiValues({ values: countyData })
          : "";
      case "status":
        return user.status || "";
      case "last_login":
        return user?.last_login ? user.last_login : "";
      default:
        return user[columnKey] || "";
    }
  };

  const fetchUserTypes = useCallback(
    async (type, currentSortConfig) => {
      setLoading(true);
      try {
        const column = USER_COULUMNS.find(
          (c) => c.columnKey === currentSortConfig.key,
        );
        const sortParamKey = column?.sortKey || currentSortConfig.key;
        console.log("meta is", meta);
        const response = await getUserByType({
          ...meta,
          user_type: type,
          sort: sortParamKey,
          order: currentSortConfig.direction,
        });

        const userKey = getUserKey(response, type);
        let fetchedUsers = [];

        if (userKey && Array.isArray(response[userKey])) {
          fetchedUsers = response[userKey];
        } else if (Array.isArray(response[type])) {
          fetchedUsers = response[type];
        } else {
          fetchedUsers = [];
        }

        const sortedData = sortUsers(
          fetchedUsers,
          sortParamKey,
          currentSortConfig.direction,
        );

        setUsers(sortedData);
        setTotalCount(response?.meta.total_count);
      } catch (error) {
        console.error("Fetching user types failed:", error);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    },
    [USER_COULUMNS, meta.page, meta.search],
  );

  const debounceFn = (callback, delay) => {
    clearTimeout(timeOut);
    timeOut = setTimeout(callback, delay);
  };

  const handleSearchQuery = (value) => {
    setMeta((prev) => ({ ...prev, search: value }));
    debounceFn(handleRefreshPage, DEBOUNCE_DELAY);
  };

  useEffect(() => {
    fetchUserTypes(userType, sortConfig);
  }, [refreshPage, userType, sortConfig.key, sortConfig.direction]);

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  const handleCheckboxChange = (index) => {
    setSelectedUsers((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length && users.length > 0) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map((_, index) => index));
    }
  };

  const handleDelete = async () => {
    try {
      const idsToDelete = selectedUsers.map((i) => users[i].id);
      setLoading(true);
      await deleteBulkUser(idsToDelete);
      setSelectedUsers([]);
      await fetchUserTypes(userType, sortConfig);
    } catch (error) {
      console.error("Error deleting users:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSingleDelete = async (userId) => {
    try {
      await deleteUser(userId);
      await fetchUserTypes(userType, sortConfig);
    } catch (error) {
      console.error("Error deleting user:", error);
    }
  };

  const handleUserSubmission = async (values, setSubmitting, setErrors) => {
    try {
      const isEditMode = Boolean(currentUserEdit);
      const payload = { ...values };

      if (!payload.user.password) delete payload.user.password;
      if (!payload.user.password_confirmation)
        delete payload.user.password_confirmation;

      if (isEditMode) {
        payload.user_type = payload.user_type;
      }

      let response;

      if (isEditMode) {
        response = await updateUser(payload, currentUserEdit.id, setErrors);
      } else {
        response = await createUser(payload, setErrors);
      }

      if (response) {
        setSubmitting(false);
        setAddUserModalVisible(false);
        setCurrentUserEdit(null);
        await fetchUserTypes(userType, sortConfig);
        try {
          if (isEditMode) {
            const loggedInUser = getUserInfo();
            // Compare with edit form email (if changed) or id (if unique)
            if (
              loggedInUser?.email &&
              currentUserEdit?.email &&
              loggedInUser.email === currentUserEdit.email
            ) {
              alert(
                "Your profile was updated. For security, you will be logged out shortly.",
              ); // Force logout after 3 seconds (3000ms)
              setTimeout(() => {
                if (logoutHandler) logoutHandler();
              }, 3000);
            }
          }
        } catch (err) {
          // No-op on force logout check
        }
      }
    } catch (error) {
      if (error?.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
      console.error("Submission error:", error);
    }
  };

  const handleCloseUserModal = () => {
    setAddUserModalVisible(false);
    setCurrentUserEdit(null);
  };

  const handleOpenUserModal = () => {
    setAddUserModalVisible(true);
  };
  const handlePageChange = (value) => {
    setMeta((pre) => ({ ...pre, page: value }));
    handleRefreshPage();
  };
  return (
    <div>
      <TableWrapper
        searchPlaceholder="Search User"
        setSearhQuery={(query) => {
          handleSearchQuery(query);
        }}
        searchValue={meta.search}
        totalListCount={totalCount}
        pageSize={meta.perPage}
        currentPage={meta.page}
        onPageChange={handlePageChange}
        selectedItems={selectedUsers.length}
        isLoading={loading}
        meta={meta}
        setMeta={setMeta}
        actionButtons={
          selectedUsers.length > 0
            ? [
                {
                  label: `Delete ${selectedUsers.length} Items`,
                  classes: "danger-btn m-0-img",
                  icon: <TrashSimple size={24} className="me-3" />,
                  handleClick: handleDelete,
                },
              ]
            : [
                {
                  label: "Add User",
                  classes: "primary-btn",
                  icon: <Plus size={24} className="me-3" />,
                  handleClick: handleOpenUserModal,
                },
              ]
        }
      >
        <div className="container-fluid">
          <div className="overflow-scroll border-radius-20px">
            <table className="table table-rows">
              <thead
                className="table-header"
                style={{ backgroundColor: "black" }}
              >
                <tr>
                  <th>
                    <div className="form-check ps-3 mb-0">
                      <input
                        className="form-check-input light-grey-background"
                        type="checkbox"
                        checked={
                          selectedUsers.length === users.length &&
                          users.length > 0
                        }
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  {USER_COULUMNS.map((item, index) => (
                    <SortableTableHeader
                      key={index}
                      columnKey={item.columnKey}
                      label={item.label}
                      isSortable={item.isSortable}
                      onSortChange={handleSort}
                      sortConfig={sortConfig}
                    />
                  ))}
                  <th></th>
                </tr>
              </thead>
              <tbody className="table-body">
                {users.map((user, index) => (
                  <tr key={index}>
                    <td>
                      <div className="form-check ps-3 mb-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={selectedUsers.includes(index)}
                          onChange={() => handleCheckboxChange(index)}
                        />
                      </div>
                    </td>
                    {USER_COULUMNS.map((item, colIndex) => (
                      <td
                        key={colIndex}
                        className={
                          item.columnKey === "full_name" ? "main-heading" : ""
                        }
                      >
                        {getUserValue(user, item.columnKey)}
                      </td>
                    ))}
                    <td>
                      {/* <Tooltip title="Delete">
                        <Trash
                          size={24}
                          className="opacity-50 me-4"
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSingleDelete(user.id)}
                        />
                      </Tooltip> */}
                      <Tooltip title="Edit">
                        <Pencil
                          size={24}
                          className="opacity-50 me-4"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setCurrentUserEdit(user);
                            handleOpenUserModal();
                          }}
                        />
                      </Tooltip>

                      {/* Change Password */}
                      <Tooltip title="Change Password">
                        <Key
                          size={24}
                          className="ms-3 opacity-50"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setSelectedUser(user);
                            toggleForgetPasswordModal(true);
                          }}
                        />
                      </Tooltip>

                      {/* Account Confirmation */}
                      {isUserConfirmed(user) ? (
                        <Tooltip title="Account Confirmed">
                          <Check
                            size={24}
                            className="ms-3 text-success"
                            weight="bold"
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Resend Confirmation Email">
                          <UploadSimple
                            size={24}
                            className="ms-3 opacity-50"
                            style={{ cursor: "pointer" }}
                            onClick={async () => {
                              await sendUserConfirmationEmail(user.id);
                              handleRefreshPage(); // optional refresh
                            }}
                          />
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {users.length === 0 && !loading && (
              <div className="text-center p-4">No users found.</div>
            )}
          </div>
        </div>
      </TableWrapper>

      {addUserModalVisible && (
        <CustomModal
          size="sm"
          show
          onHide={handleCloseUserModal}
          heading={currentUserEdit ? "Edit User" : "Add User"}
        >
          <AddUserForm
            currentUserEdit={currentUserEdit}
            handleSubmition={handleUserSubmission}
            handleClose={handleCloseUserModal}
            currentUserType={userType}
          />
        </CustomModal>
      )}
      <CustomModal
        size="sm"
        show={isForgetPasswordModalVisisble}
        onHide={() => {
          setSelectedUser(undefined);
          toggleForgetPasswordModal();
        }}
        heading="Change Password"
      >
        <ChangePasswordForm
          handleSubmit={(values) => {
            updateUserPassword(selectedUser.id, {
              password: values.password,
              password_confirmation: values.confirmPassword,
            })
              .then(() => {
                toggleForgetPasswordModal();
                setSelectedUser(undefined);
              })
              .catch((_) => {
                toast(
                  <CustomToast
                    variant={alertVariants.DANGER}
                    message="Failed to update user password"
                  />,
                );
              });
          }}
          handleClose={() => {
            setSelectedUser(undefined);
            toggleForgetPasswordModal();
          }}
          currentOutreachStaffEdit={selectedUser}
        />
      </CustomModal>
    </div>
  );
};

export default Users;
